import React from 'react';
import { FileText, Pencil } from 'lucide-react';
import { STEP_LABELS, LICENSE_CLASS_OPTIONS, ENDORSEMENT_OPTIONS, EQUIPMENT_OPTIONS } from './stepConfig';
import { formatPhoneInput, formatDisplayDate, formatMonthLabel } from '../../utils/dateInput';
import { getStateLabel } from '../../utils/usStates';

const dash = (value) => (value === null || value === undefined || value === '' ? '—' : value);
const yn = (value) => (value === true ? 'Yes' : value === false ? 'No' : '—');
const label = (options, value) => options.find((o) => o.value === value)?.label || value || '—';
const date = (value) => (value ? formatDisplayDate(value) : '—');
const phone = (value) => (value ? formatPhoneInput(value) : '—');
const month = (value) => dash(formatMonthLabel(value));
const range = (entry) => `${month(entry?.from)} to ${entry?.isPresent ? 'Present' : month(entry?.to)}`;

export const Rows = ({ rows, columns = 2 }) => (
  <dl className={`grid grid-cols-1 gap-x-6 gap-y-3 ${columns === 2 ? 'sm:grid-cols-2' : columns === 3 ? 'sm:grid-cols-3' : ''}`}>
    {rows.filter(Boolean).map(([term, value, span]) => (
      <div key={term} className={`min-w-0 ${span ? 'sm:col-span-full' : ''}`}>
        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{term}</dt>
        <dd className="mt-0.5 whitespace-pre-line text-sm text-gray-900 [overflow-wrap:anywhere]">{dash(value)}</dd>
      </div>
    ))}
  </dl>
);

export const SummaryCard = ({ stepId, title, onEdit, children, badge, headerAction, highlighted = false }) => (
  <section id={`section-${stepId}`} className={`scroll-mt-24 rounded-xl border bg-white shadow-sm ${highlighted ? 'border-amber-400 ring-2 ring-amber-100' : 'border-gray-200'}`} aria-labelledby={`summary-${stepId}`}>
    <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 sm:px-5">
      <h3 id={`summary-${stepId}`} className="flex items-center gap-2 text-base font-semibold text-gray-900">{title}{badge}</h3>
      {headerAction}
      {onEdit && (
        <button type="button" onClick={() => onEdit(stepId)} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
          <Pencil className="h-3.5 w-3.5" aria-hidden="true" /> Edit
        </button>
      )}
    </div>
    <div className="space-y-4 px-4 py-4 sm:px-5">{children}</div>
  </section>
);

const SubList = ({ title, items, render, empty = 'None' }) => (
  <div>
    {title && <p className="mb-2 text-sm font-semibold text-gray-800">{title}</p>}
    {items?.length ? (
      <ul className="space-y-3">
        {items.map((item, index) => (
          <li key={item.id || index} className="rounded-lg border border-gray-100 bg-gray-50 p-3">{render(item, index)}</li>
        ))}
      </ul>
    ) : <p className="text-sm text-gray-500">{empty}</p>}
  </div>
);

export const PersonalSummary = ({ application, onEdit, revealSsn, cardProps }) => {
  const p = application.sections?.personal || {};
  const ec = p.emergencyContact || {};
  return (
    <SummaryCard stepId="personal" title={STEP_LABELS.personal} onEdit={onEdit} {...cardProps}>
      <Rows rows={[
        ['Name', [p.firstName, p.middleName, p.lastName].filter(Boolean).join(' ')],
        ['Date of birth', date(p.dateOfBirth)],
        ['Phone', phone(p.phone)],
        ['Email', application.applicant?.email],
        ['Social Security Number', revealSsn || (p.ssnLast4 ? `•••-••-${p.ssnLast4}` : '—')],
        ['Current address', [p.address, p.city, p.state, p.zipCode].filter(Boolean).join(', ')],
        ['Living there since', formatMonthLabel(p.currentAddressSince)],
        ['TWIC card', `${yn(p.hasTwicCard)}${p.hasTwicCard && p.twicExpiration ? ` (expires ${date(p.twicExpiration)})` : ''}`],
        ['Passport', yn(p.hasPassport)],
        ['Emergency contact', ec.name ? `${ec.name} (${ec.relationship || '—'}), ${phone(ec.phone)}` : '—']
      ]} />
      <SubList title="Previous addresses (last 3 years)" items={application.sections?.residences} empty="None (lived at current address for the full period)" render={(r) => (
        <p className="text-sm text-gray-900">{[r.address, r.city, r.state, r.zipCode].filter(Boolean).join(', ')} <span className="text-gray-500">· {month(r.from)} to {month(r.to)}</span></p>
      )} />
    </SummaryCard>
  );
};

export const LicenseSummary = ({ application, onEdit, showDocuments = true, cardProps }) => {
  const l = application.sections?.licenses || {};
  const x = application.sections?.experience || {};
  const docs = application.documents || [];
  return (
    <SummaryCard stepId="license" title={STEP_LABELS.license} onEdit={onEdit} {...cardProps}>
      <SubList title="Licenses held (last 3 years)" items={l.items} render={(item) => (
        <div>
          <p className="text-sm font-medium text-gray-900">
            {[item.firstNameOnLicense, item.middleNameOnLicense, item.lastNameOnLicense].filter(Boolean).join(' ')} · {item.licenseNumber} · {getStateLabel(item.state)}
            {item.isCurrent && <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">Current</span>}
          </p>
          <p className="mt-0.5 text-xs text-gray-600">{label(LICENSE_CLASS_OPTIONS, item.licenseClass)} · issued {date(item.issued)} · expires {date(item.expires)} · endorsements: {(item.endorsements || []).map((e) => label(ENDORSEMENT_OPTIONS, e).split(' - ')[0]).join(', ') || 'none'}</p>
        </div>
      )} />
      <Rows rows={[
        ['Single-license certification', l.singleLicenseCertification ? 'Certified' : 'Not certified'],
        ['Medical card expires', date(l.medicalCardExpiration)],
        ['Driven a CMV before', yn(x.hasDrivenCmvBefore)]
      ]} columns={3} />
      <SubList title="Equipment operated" items={x.equipment} empty="None listed" render={(e) => (
        <p className="text-sm text-gray-900">{label(EQUIPMENT_OPTIONS, e.type)}{e.type === 'other' && x.otherDescription ? ` (${x.otherDescription})` : ''} <span className="text-gray-500">· {range(e)}{e.approxMiles ? ` · ~${Number(e.approxMiles).toLocaleString()} miles` : ''}</span></p>
      )} />
      {showDocuments && (
        <div>
          <p className="mb-2 text-sm font-semibold text-gray-800">Documents</p>
          {docs.length ? (
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {docs.map((doc) => (
                <li key={doc.id} className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                  <a href={doc.previewUrl || '#'} target="_blank" rel="noreferrer" className="block">
                    <div className="flex h-24 items-center justify-center bg-gray-100">
                      {doc.previewUrl && (doc.mimeType || '').startsWith('image/')
                        ? <img src={doc.previewUrl} alt="" className="h-full w-full object-cover" />
                        : <FileText className="h-8 w-8 text-gray-400" aria-hidden="true" />}
                    </div>
                    <p className="truncate px-2 py-1.5 text-xs font-medium text-gray-800">{doc.label}</p>
                  </a>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-gray-500">No documents uploaded</p>}
        </div>
      )}
    </SummaryCard>
  );
};

export const RecordSummary = ({ application, onEdit, cardProps }) => {
  const r = application.sections?.drivingRecord || {};
  return (
    <SummaryCard stepId="record" title={STEP_LABELS.record} onEdit={onEdit} {...cardProps}>
      <Rows rows={[
        ['Accidents in last 3 years', yn(r.accidents?.hadAny)],
        ['Violations in last 3 years', yn(r.violations?.hadAny)],
        ['Ever denied a license', yn(r.forfeitures?.deniedLicense)],
        ['License suspended or revoked', yn(r.forfeitures?.revokedOrSuspended)],
        r.forfeitures?.details ? ['Circumstances', r.forfeitures.details, true] : null
      ]} />
      {r.accidents?.hadAny && (
        <SubList title="Accidents" items={r.accidents.items} render={(a) => (
          <p className="text-sm text-gray-900">{date(a.date)} · {a.location} · {a.description} <span className="text-gray-500">· fatalities {a.fatalities ?? 0}, injuries {a.injuries ?? 0}, hazmat spill {yn(a.hazmatSpill)}, CMV {yn(a.commercialVehicle)}</span></p>
        )} />
      )}
      {r.violations?.hadAny && (
        <SubList title="Violations" items={r.violations.items} render={(v) => (
          <p className="text-sm text-gray-900">{date(v.date)} · {v.offense} · {v.location} <span className="text-gray-500">· {v.vehicleType === 'cmv' ? 'commercial vehicle' : 'personal vehicle'}{v.penalty ? ` · ${v.penalty}` : ''}</span></p>
        )} />
      )}
    </SummaryCard>
  );
};

export const EmploymentSummary = ({ application, onEdit, cardProps }) => {
  const e = application.sections?.employment || {};
  const rtd = { yes: 'Yes', no: 'No', na: 'N/A' };
  return (
    <SummaryCard stepId="employment" title={STEP_LABELS.employment} onEdit={onEdit} {...cardProps}>
      <Rows rows={[
        ['Tested positive or refused a pre-employment test (2 yrs)', yn(e.drugAlcohol?.testedPositiveOrRefusedLast2Years)],
        ['Can provide return-to-duty documentation', rtd[e.drugAlcohol?.canProvideReturnToDutyDocs] || '—']
      ]} />
      <SubList title="Employers" items={e.employers} render={(emp) => (
        <div>
          <p className="text-sm font-medium text-gray-900">{emp.employerName} <span className="font-normal text-gray-500">· {range(emp)}</span></p>
          <p className="mt-0.5 text-xs text-gray-600">{emp.positionHeld}{emp.isPresent ? ' (current)' : emp.reasonForLeaving ? ` · left: ${emp.reasonForLeaving}` : ''} · {phone(emp.phone)}{emp.city ? ` · ${[emp.city, emp.state].filter(Boolean).join(', ')}` : ''} · DOT regulated {yn(emp.dotRegulated)} · safety-sensitive {yn(emp.safetySensitive)}</p>
        </div>
      )} />
      <Rows rows={[['Employment gaps', e.gapExplanation || 'None', true]]} />
    </SummaryCard>
  );
};

/**
 * Full read-only rendering used by the applicant's review step and the admin detail page.
 */
const ApplicationSummary = ({ application, onEdit, showDocuments = true, revealSsn }) => (
  <div className="space-y-5">
    <PersonalSummary application={application} onEdit={onEdit} revealSsn={revealSsn} />
    <LicenseSummary application={application} onEdit={onEdit} showDocuments={showDocuments} />
    <RecordSummary application={application} onEdit={onEdit} />
    <EmploymentSummary application={application} onEdit={onEdit} />
  </div>
);

export default ApplicationSummary;
